import type { LengthUnit } from '@fulcrum/lib';
import { convertLength } from '@trimble/utilities';
import { hexToRgb, rgbToHex } from '@trimble/ui-components/solidjs';
import type { TextureType } from './types/heatmap.types';

export const FULL_PERCENT = 100;
export const OVERFLOW_GREY = '#5c5c5c';
export const STOP_ROW_HEIGHT_PX = 40;
export const GRADIENT_STOP_BAR_WIDTH_PX = 44;
export const GRADIENT_STOP_COLUMN_WIDTH_PX = 44;
export const GRADIENT_STOP_BAND_OFFSET_PX = 6;
export const COLOR_SWATCH_SIZE_PX = 24;
export const COLOR_SWATCH_WRAPPER_SIZE_PX = 28;
export const COLOR_SWATCH_PADDING_PX = 2;
export const COLOR_SWATCH_BORDER_RADIUS_PX = 4;
export const OVERFLOW_CAP_WIDTH_PERCENT = 10;

/** Top edge of a swatch when it is vertically centred in a stop row slot. */
export function stopRowSwatchTopPx(rowTopPx: number): number {
  return (
    rowTopPx +
    (STOP_ROW_HEIGHT_PX - COLOR_SWATCH_WRAPPER_SIZE_PX) / HALF_DIVISOR
  );
}

/** Vertical centre of a swatch centred in a stop row slot. */
export function stopRowSwatchCenterTopPx(rowTopPx: number): number {
  return (
    stopRowSwatchTopPx(rowTopPx) + COLOR_SWATCH_WRAPPER_SIZE_PX / HALF_DIVISOR
  );
}

const HALF_DIVISOR = 2;
const HALF_PERCENT = FULL_PERCENT / HALF_DIVISOR;
const BOUND_VALUE_DECIMAL_PLACES = 3;
/** Decimal places when persisting metres for display-unit round-trip (not display formatting). */
const METER_STORAGE_DECIMAL_PLACES = 10;
/** Above + below overflow rows in the stop list column. */
const OVERFLOW_CAP_ROW_COUNT = 2;

/** Options for aligning stop rows, handles, and editor bar paint. */
export interface StopLayoutOptions {
  readonly textureType: TextureType;
  /**
   * When true, enforces {@link STOP_ROW_HEIGHT_PX} minimum spacing between rows
   * (for future editable layouts). When false, rows follow value-proportional
   * ideal positions so the viewer bar and list stay aligned (Figma).
   */
  readonly clampOverlappingRows: boolean;
}

export const VIEWER_STOP_LAYOUT: Omit<StopLayoutOptions, 'textureType'> = {
  clampOverlappingRows: false,
};

export const EDITABLE_STOP_LAYOUT: Omit<StopLayoutOptions, 'textureType'> = {
  clampOverlappingRows: true,
};

function resolveStopLayoutOptions(
  textureType: TextureType,
  options?: Partial<StopLayoutOptions>
): StopLayoutOptions {
  return {
    textureType,
    clampOverlappingRows:
      options?.clampOverlappingRows ??
      EDITABLE_STOP_LAYOUT.clampOverlappingRows,
  };
}

export function parseStopValue(value: string): number {
  if (value === '-' || value.trim() === '') return 0;
  const parsed = parseFloat(value);
  return isNaN(parsed) ? 0 : parsed;
}

export interface StopValueRange {
  readonly min: number;
  readonly max: number;
}

export function stopValueRange(
  stops: readonly { value: string }[]
): StopValueRange {
  const values = stops.map((stop) => parseStopValue(stop.value));
  if (values.length === 0) {
    return { min: 0, max: 0 };
  }
  return { min: Math.min(...values), max: Math.max(...values) };
}

/** Maps a stop value to a vertical percent (0 = top, 100 = bottom). Higher values sit toward the top. */
export function valueToVerticalPercent(
  value: number,
  range: StopValueRange
): number {
  if (range.max === range.min) {
    return HALF_PERCENT;
  }
  return ((range.max - value) / (range.max - range.min)) * FULL_PERCENT;
}

export function sortStopsByValueDescending<T extends { value: string }>(
  stops: readonly T[]
): T[] {
  return [...stops].sort(
    (a, b) => parseStopValue(b.value) - parseStopValue(a.value)
  );
}

export interface OverflowColorOverrides {
  readonly above?: string | null;
  readonly below?: string | null;
}

export function overflowStopColors(
  stops: readonly { color: string; value: string }[],
  overrides?: OverflowColorOverrides,
  textureType?: TextureType
): { above: string; below: string } {
  const sorted = sortStopsByValueDescending(stops);
  // Stepped mirrors the tile renderer: the lowest stop owns every value at or below its
  // own value, so there is no separate out-of-range-below colour. The below override is
  // ignored for stepped and the lowest stop colour fills the tail. Gradient keeps its
  // out-of-range-below override, and the above cap is unchanged for both textures.
  const belowOverride =
    textureType === 'stepped' ? undefined : overrides?.below;
  return {
    above: overrides?.above ?? sorted[0]?.color ?? OVERFLOW_GREY,
    below: belowOverride ?? sorted.at(-1)?.color ?? OVERFLOW_GREY,
  };
}

export function formatOverflowLabel(
  bound: 'above' | 'below',
  boundValue: number,
  unit: string
): string {
  const prefix = bound === 'above' ? '>' : '<';
  return `${prefix} ${formatBoundValue(boundValue)} ${unit}`;
}

function formatBoundValue(value: number): string {
  if (Number.isInteger(value)) {
    return String(value);
  }
  return value.toFixed(BOUND_VALUE_DECIMAL_PLACES).replace(/\.?0+$/, '');
}

function roundToBoundPrecision(value: number): number {
  return parseFloat(value.toFixed(BOUND_VALUE_DECIMAL_PLACES));
}

/** Persist metres with enough precision to round-trip the active display unit. */
function formatMeterValueForStorage(valueInMeters: number): string {
  if (Number.isInteger(valueInMeters)) {
    return String(valueInMeters);
  }
  return valueInMeters
    .toFixed(METER_STORAGE_DECIMAL_PLACES)
    .replace(/\.?0+$/, '');
}

/**
 * Convert a stop value from the canonical store unit (metres) into the active
 * display unit. Stops are stored in metres to satisfy the measurements API's
 * strict-descending contract, so this conversion is display-only
 */
export function convertStopValueToDisplay(
  valueInMeters: number,
  displayUnit: LengthUnit
): number {
  return displayUnit === 'm'
    ? valueInMeters
    : convertLength(valueInMeters, 'm', displayUnit);
}

/**
 * Format a stored (metre) stop value string for display in the active unit.
 * Metric projects keep the authored string as-is (preserving trailing zeros);
 * other units are converted and formatted to {@link BOUND_VALUE_DECIMAL_PLACES}.
 */
export function formatStopValueForDisplay(
  valueInMeters: string,
  displayUnit: LengthUnit
): string {
  if (displayUnit === 'm') {
    return valueInMeters;
  }
  return formatBoundValue(
    convertLength(parseStopValue(valueInMeters), 'm', displayUnit)
  );
}

/**
 * Format a canonical metre value for persistence on an EditableStop.
 * Metric projects round in metres; other units round in the project display unit
 * first so values like 2 ft do not become 0.610 m and display as 2.001 ft.
 */
export function formatStopValueInMeters(
  valueInMeters: number,
  displayUnit: LengthUnit
): string {
  if (displayUnit === 'm') {
    return formatBoundValue(valueInMeters);
  }

  const roundedDisplay = roundToBoundPrecision(
    convertLength(valueInMeters, 'm', displayUnit)
  );
  const canonicalMeters = convertLength(roundedDisplay, displayUnit, 'm');
  return formatMeterValueForStorage(canonicalMeters);
}

/**
 * Parse a user-typed display value back into canonical metres, or `null` when the
 * input is empty, incomplete, or not numeric.
 */
export function parseDisplayStopValueToMeters(
  display: string,
  displayUnit: LengthUnit
): number | null {
  const trimmed = display.trim();
  if (trimmed === '' || trimmed === '-') return null;
  const parsed = Number(trimmed);
  if (!Number.isFinite(parsed)) return null;
  return displayUnit === 'm' ? parsed : convertLength(parsed, displayUnit, 'm');
}

const RGB_RED_CHANNEL = 0;
const RGB_GREEN_CHANNEL = 1;
const RGB_BLUE_CHANNEL = 2;

function lerpRgb(
  from: [number, number, number],
  to: [number, number, number],
  t: number
): string {
  const mix = (a: number, b: number): number => Math.round(a + (b - a) * t);
  return rgbToHex([
    mix(from[RGB_RED_CHANNEL], to[RGB_RED_CHANNEL]),
    mix(from[RGB_GREEN_CHANNEL], to[RGB_GREEN_CHANNEL]),
    mix(from[RGB_BLUE_CHANNEL], to[RGB_BLUE_CHANNEL]),
  ]);
}

/**
 * Interpolate the gradient colour at `valueInMeters` between the two nearest
 * stops (RGB lerp). Values outside the stop range clamp to the endpoint colour.
 */
export function colorAtValue(
  valueInMeters: number,
  stops: readonly { color: string; value: string }[]
): string {
  if (stops.length === 0) {
    return OVERFLOW_GREY;
  }
  if (stops.length === 1) {
    return stops[0].color;
  }

  const sorted = [...stops].sort(
    (a, b) => parseStopValue(a.value) - parseStopValue(b.value)
  );
  const minValue = parseStopValue(sorted[0].value);
  const maxValue = parseStopValue(sorted[sorted.length - 1].value);

  if (valueInMeters <= minValue) {
    return sorted[0].color;
  }
  if (valueInMeters >= maxValue) {
    return sorted[sorted.length - 1].color;
  }

  for (let index = 0; index < sorted.length - 1; index++) {
    const lowerValue = parseStopValue(sorted[index].value);
    const upperValue = parseStopValue(sorted[index + 1].value);
    if (valueInMeters >= lowerValue && valueInMeters <= upperValue) {
      if (upperValue === lowerValue) {
        return sorted[index].color;
      }
      const t = (valueInMeters - lowerValue) / (upperValue - lowerValue);
      return lerpRgb(
        hexToRgb(sorted[index].color),
        hexToRgb(sorted[index + 1].color),
        t
      );
    }
  }

  return sorted[sorted.length - 1].color;
}

export function heatmapValueGradientCss(
  stops: readonly { color: string; value: string }[],
  direction: 'to bottom' | 'to right' = 'to bottom'
): string {
  if (stops.length === 0) {
    return OVERFLOW_GREY;
  }
  const range = stopValueRange(stops);
  const sorted = [...stops].sort(
    (a, b) => parseStopValue(a.value) - parseStopValue(b.value)
  );
  const parts = sorted.map(
    (stop) =>
      `${stop.color} ${valueToVerticalPercent(parseStopValue(stop.value), range).toFixed(1)}%`
  );
  // Value-sorted stops map to decreasing percents (high value at top); CSS requires increasing order.
  return `linear-gradient(${direction}, ${parts.reverse().join(', ')})`;
}

/**
 * Smooth gradient for the palette editor bar. Colours are placed at layout
 * breakpoints (aligned with handles) rather than raw values so clustered stops
 * still appear on the bar.
 */
export function heatmapLayoutGradientCss(
  stops: readonly { id: string; color: string; value: string }[],
  direction: 'to bottom' | 'to right' = 'to bottom'
): string {
  if (stops.length === 0) {
    return OVERFLOW_GREY;
  }

  const layoutStops = stops
    .map((stop) => ({
      color: stop.color,
      pct: stopBreakVerticalPercent(stop, stops, 'gradient'),
    }))
    .sort((a, b) => a.pct - b.pct);

  const parts = layoutStops.map(
    (stop) => `${stop.color} ${stop.pct.toFixed(1)}%`
  );
  return `linear-gradient(${direction}, ${parts.join(', ')})`;
}

export function heatmapValueSteppedCss(
  stops: readonly { color: string; value: string }[],
  direction: 'to bottom' | 'to right' = 'to bottom'
): string {
  if (stops.length === 0) {
    return OVERFLOW_GREY;
  }
  const range = stopValueRange(stops);
  const sorted = [...stops].sort(
    (a, b) => parseStopValue(a.value) - parseStopValue(b.value)
  );
  const parts: string[] = [];

  for (let index = 0; index < sorted.length; index++) {
    const { bottomPct, topPct } = steppedBandEdges(index, sorted, range);
    const stop = sorted[index];
    parts.push(
      `${stop.color} ${bottomPct.toFixed(1)}%`,
      `${stop.color} ${topPct.toFixed(1)}%`
    );
  }
  // Value-sorted band edges are emitted bottom-to-top; CSS linear-gradient needs increasing percents.
  return `linear-gradient(${direction}, ${parts.reverse().join(', ')})`;
}

interface SteppedBandEdges {
  readonly bottomPct: number;
  readonly topPct: number;
}

/**
 * Top-anchored stepped band edges (matches the tile renderer). A stop's colour band
 * starts at the stop's own value (the band TOP) and fills down to the next lower stop's value
 * (the band BOTTOM); the lowest stop owns the tail down to the bottom of the bar. This mirrors
 * how TRex renders `UseSolidColors` (a stop's colour applies from its delta down to the next
 * lower stop), so the legend, handles and painted bar line up with the rendered tiles.
 */
function steppedBandEdges(
  ascendIndex: number,
  sorted: readonly { value: string }[],
  range: StopValueRange
): SteppedBandEdges {
  const stopPct = valueToVerticalPercent(
    parseStopValue(sorted[ascendIndex].value),
    range
  );
  const bottomPct =
    ascendIndex === 0
      ? FULL_PERCENT
      : valueToVerticalPercent(
          parseStopValue(sorted[ascendIndex - 1].value),
          range
        );

  return { bottomPct, topPct: stopPct };
}

/**
 * Band centre for the gradient preview ramp. Retains the original midpoint model so the gradient
 * texture is completely unaffected by the stepped (top-anchored) alignment fix.
 */
function gradientBandCentrePct(
  ascendIndex: number,
  sorted: readonly { value: string }[],
  range: StopValueRange
): number {
  const stopPct = valueToVerticalPercent(
    parseStopValue(sorted[ascendIndex].value),
    range
  );
  const previousPct =
    ascendIndex === 0
      ? FULL_PERCENT
      : valueToVerticalPercent(
          parseStopValue(sorted[ascendIndex - 1].value),
          range
        );
  const nextPct =
    ascendIndex === sorted.length - 1
      ? 0
      : valueToVerticalPercent(
          parseStopValue(sorted[ascendIndex + 1].value),
          range
        );

  const bottomPct =
    ascendIndex === 0 ? FULL_PERCENT : (previousPct + stopPct) / HALF_DIVISOR;
  const topPct =
    ascendIndex === sorted.length - 1 ? 0 : (stopPct + nextPct) / HALF_DIVISOR;

  return (bottomPct + topPct) / HALF_DIVISOR;
}

/** Height of the overflow + stop list column that aligns with the gradient bar. */
export function stopListHeightPx(editableStopCount: number): number {
  return (editableStopCount + OVERFLOW_CAP_ROW_COUNT) * STOP_ROW_HEIGHT_PX;
}

/**
 * Maps a vertical percent within the editable stop range to a pixel offset from
 * the top of the stop list (overflow cap + value-scaled middle section).
 */
export function stopBreakTopPx(
  verticalPercent: number,
  editableStopCount: number
): number {
  const middleHeightPx = editableStopCount * STOP_ROW_HEIGHT_PX;
  return STOP_ROW_HEIGHT_PX + (verticalPercent / FULL_PERCENT) * middleHeightPx;
}

/** Ideal row top for a stop before overlap clamping. */
export function stopRowIdealTopPx(
  stop: { id: string; value: string },
  stops: readonly { id: string; value: string }[]
): number {
  const range = stopValueRange(stops);
  const verticalPercent = valueToVerticalPercent(
    parseStopValue(stop.value),
    range
  );
  const { minTopPx, maxTopPx } = stopRowTopBoundsPx(stops.length);
  return minTopPx + (verticalPercent / FULL_PERCENT) * (maxTopPx - minTopPx);
}

/** Top offset of the overflow-above row (matches the bar overflow cap). */
export function overflowAboveRowTopPx(): number {
  return 0;
}

/** Top offset of the overflow-below row (matches the bar overflow cap). */
export function overflowBelowRowTopPx(editableStopCount: number): number {
  return stopListHeightPx(editableStopCount) - STOP_ROW_HEIGHT_PX;
}

/**
 * Editable stop row top range inside the list column — between overflow caps.
 */
export function stopRowTopBoundsPx(editableStopCount: number): {
  readonly minTopPx: number;
  readonly maxTopPx: number;
} {
  return {
    minTopPx: STOP_ROW_HEIGHT_PX,
    maxTopPx: overflowBelowRowTopPx(editableStopCount) - STOP_ROW_HEIGHT_PX,
  };
}

/**
 * Value-proportional row tops for handles, bar anchors, and value-column rows.
 */
function stopValueRowTopPxMap(
  stops: readonly { id: string; value: string }[],
  layout: StopLayoutOptions
): ReadonlyMap<string, number> {
  const sorted = sortStopsByValueDescending(stops);
  const { minTopPx, maxTopPx } = stopRowTopBoundsPx(stops.length);

  const equalSpacingRows = (): ReadonlyMap<string, number> =>
    new Map(
      sorted.map((stop, index) => [
        stop.id,
        minTopPx + index * STOP_ROW_HEIGHT_PX,
      ])
    );

  const clampToBounds = (topPx: number): number =>
    Math.min(Math.max(topPx, minTopPx), maxTopPx);

  if (!layout.clampOverlappingRows) {
    return new Map(
      sorted.map((stop) => [
        stop.id,
        clampToBounds(stopRowIdealTopPx(stop, stops)),
      ])
    );
  }

  const lowestStop = sorted.at(-1);
  if (
    lowestStop !== undefined &&
    stopRowIdealTopPx(lowestStop, stops) > maxTopPx
  ) {
    return equalSpacingRows();
  }

  const topsById = new Map<string, number>();
  let previousTopPx = minTopPx - STOP_ROW_HEIGHT_PX;

  for (const stop of sorted) {
    const topPx = Math.max(
      stopRowIdealTopPx(stop, stops),
      previousTopPx + STOP_ROW_HEIGHT_PX
    );
    if (topPx > maxTopPx) {
      return equalSpacingRows();
    }
    topsById.set(stop.id, topPx);
    previousTopPx = topPx;
  }

  return topsById;
}

/**
 * Row tops that follow handle layout but never overlap adjacent rows. When
 * handles are tighter than {@link STOP_ROW_HEIGHT_PX}, lower rows shift down.
 * Falls back to equal slot spacing if the ideal layout cannot fit above the
 * bottom overflow row.
 */
export function stopRowTopPxMap(
  stops: readonly { id: string; value: string }[],
  textureType: TextureType,
  options?: Partial<StopLayoutOptions>
): ReadonlyMap<string, number> {
  const layout = resolveStopLayoutOptions(textureType, options);
  return stopValueRowTopPxMap(stops, layout);
}

/** Row top for bar markers and handles. */
function stopValueLayoutRowTopPx(
  stop: { id: string; value: string },
  stops: readonly { id: string; value: string }[],
  layout: StopLayoutOptions
): number {
  const rowTop = stopValueRowTopPxMap(stops, layout).get(stop.id);
  if (rowTop !== undefined) {
    return rowTop;
  }
  return stopRowIdealTopPx(stop, stops);
}

/**
 * Pixel anchor on the editor bar for a stop (swatch vertical centre).
 * Stepped and gradient share the same marker positions; only bar paint differs.
 */
export function stopBarMarkerAnchorTopPx(
  stop: { id: string; value: string },
  stops: readonly { id: string; value: string }[],
  textureType: TextureType,
  options?: Partial<StopLayoutOptions>
): number {
  return stopBarColorAnchorTopPx(
    stop,
    stops,
    resolveStopLayoutOptions(textureType, options)
  );
}

/**
 * Top offset of the handle wrapper so the indicator line and dot centre sit on
 * the swatch vertical centre (same for gradient and stepped).
 */
export function stopHandleWrapperTopPx(
  stop: { id: string; value: string },
  stops: readonly { id: string; value: string }[],
  textureType: TextureType,
  options?: Partial<StopLayoutOptions>
): number {
  const anchorPx = stopBarMarkerAnchorTopPx(stop, stops, textureType, options);
  return anchorPx - GRADIENT_STOP_BAND_OFFSET_PX;
}

/**
 * Vertical percent (0 = top of middle section, 100 = bottom) for stepped handle
 * and row layout. Stepped returns the band TOP, which is the stop's own value
 * (top-anchored), so handles/rows/bar share one value-proportional scale.
 * The gradient branch returns band centres, used by {@link heatmapLayoutGradientCss}
 * for the preview ramp.
 */
export function stopBreakVerticalPercent(
  stop: { id: string; value: string },
  stops: readonly { id: string; value: string }[],
  textureType: TextureType
): number {
  const range = stopValueRange(stops);
  const sorted = [...stops].sort(
    (a, b) => parseStopValue(a.value) - parseStopValue(b.value)
  );
  const ascendIndex = sorted.findIndex((candidate) => candidate.id === stop.id);
  if (ascendIndex < 0) {
    return HALF_PERCENT;
  }
  if (textureType === 'gradient') {
    return gradientBandCentrePct(ascendIndex, sorted, range);
  }
  // Stepped: the handle/row sits at the band TOP, i.e. the stop's own value.
  return steppedBandEdges(ascendIndex, sorted, range).topPct;
}

function stopBarColorAnchorTopPx(
  stop: { id: string; value: string },
  stops: readonly { id: string; value: string }[],
  layout: StopLayoutOptions
): number {
  const rowTopPx = stopValueLayoutRowTopPx(stop, stops, layout);
  return stopRowSwatchCenterTopPx(rowTopPx);
}

export function heatmapBarCss(
  stops: readonly { color: string; value: string }[],
  textureType: TextureType
): string {
  return textureType === 'stepped'
    ? heatmapValueSteppedCss(stops, 'to bottom')
    : heatmapValueGradientCss(stops, 'to bottom');
}

interface StopLayoutSteppedBand {
  readonly color: string;
  readonly topPx: number;
  readonly bottomPx: number;
}

/** Stepped bar bands between shared layout anchors (same px as gradient handles). */
function stopLayoutSteppedBandsPx(
  stops: readonly { id: string; color: string; value: string }[],
  layout: StopLayoutOptions
): StopLayoutSteppedBand[] {
  const sortedAsc = [...stops].sort(
    (a, b) => parseStopValue(a.value) - parseStopValue(b.value)
  );
  const bottomStartPx = stopListHeightPx(stops.length) - STOP_ROW_HEIGHT_PX;
  const bandAnchorPx = sortedAsc.map((stop) =>
    stopBarColorAnchorTopPx(stop, stops, layout)
  );

  return sortedAsc.map((stop, index) => ({
    color: stop.color,
    topPx: bandAnchorPx[index],
    bottomPx: index === 0 ? bottomStartPx : bandAnchorPx[index - 1],
  }));
}

function formatBarStopPx(px: number): string {
  return `${px.toFixed(1)}px`;
}

function pushBarColorStop(parts: string[], color: string, px: number): void {
  parts.push(`${color} ${formatBarStopPx(px)}`);
}

/** Duplicate px stops so CSS does not blend distinct colours at a boundary. */
function pushBarHardEdge(
  parts: string[],
  fromColor: string,
  toColor: string,
  px: number
): void {
  pushBarColorStop(parts, fromColor, px);
  if (fromColor !== toColor) {
    pushBarColorStop(parts, toColor, px);
  }
}

/**
 * Full-height editor bar paint with pixel stops (overflow caps + middle section),
 * matching the UX prototype linear-gradient on a single track element.
 */
export function heatmapEditorBarPixelCss(
  stops: readonly { id: string; color: string; value: string }[],
  textureType: TextureType,
  overflowOverrides?: OverflowColorOverrides
): string {
  const editableStopCount = stops.length;
  if (editableStopCount === 0) {
    return OVERFLOW_GREY;
  }

  const totalHeightPx = stopListHeightPx(editableStopCount);
  const overflowPx = STOP_ROW_HEIGHT_PX;
  const bottomStartPx = totalHeightPx - overflowPx;
  const overflow = overflowStopColors(stops, overflowOverrides, textureType);
  const parts: string[] = [];

  const layout = resolveStopLayoutOptions(textureType, VIEWER_STOP_LAYOUT);

  if (textureType === 'stepped') {
    const bands = stopLayoutSteppedBandsPx(stops, layout);
    bands.sort((a, b) => a.topPx - b.topPx);
    const topBandColor = bands[0]?.color ?? overflow.above;
    const topBandTopPx = bands[0]?.topPx ?? overflowPx;
    const bottomBandColor = bands.at(-1)?.color ?? overflow.below;

    pushBarColorStop(parts, overflow.above, 0);
    pushBarHardEdge(parts, overflow.above, topBandColor, topBandTopPx);

    for (const band of bands) {
      pushBarColorStop(parts, band.color, band.topPx);
      pushBarColorStop(parts, band.color, band.bottomPx);
    }

    pushBarHardEdge(parts, bottomBandColor, overflow.below, bottomStartPx);
    pushBarColorStop(parts, overflow.below, totalHeightPx);
  } else {
    const layoutStops = stops
      .map((stop) => ({
        color: stop.color,
        px: stopBarColorAnchorTopPx(stop, stops, layout),
      }))
      .sort((a, b) => a.px - b.px);

    const firstAnchorPx = layoutStops[0]?.px ?? overflowPx;
    const lastAnchorPx = layoutStops.at(-1)?.px ?? bottomStartPx;
    const firstInRangeColor = layoutStops[0]?.color ?? overflow.above;
    const lastInRangeColor = layoutStops.at(-1)?.color ?? overflow.below;

    pushBarColorStop(parts, overflow.above, 0);
    pushBarHardEdge(parts, overflow.above, firstInRangeColor, firstAnchorPx);

    for (const stop of layoutStops) {
      pushBarColorStop(parts, stop.color, stop.px);
    }

    pushBarHardEdge(parts, lastInRangeColor, overflow.below, lastAnchorPx);
    pushBarColorStop(parts, overflow.below, totalHeightPx);
  }

  return `linear-gradient(${parts.join(', ')})`;
}

/** Bar background for the palette stop editor (handles + preview column). */
export function heatmapEditorBarCss(
  stops: readonly { id: string; color: string; value: string }[],
  textureType: TextureType,
  overflowOverrides?: OverflowColorOverrides
): string {
  return heatmapEditorBarPixelCss(stops, textureType, overflowOverrides);
}

/**
 * Maps vertical layout percent (0 = top/high, 100 = bottom/low) to horizontal
 * preview percent (0 = left/low, 100 = right/high), reserving proportional
 * overflow caps at each end.
 */
export function verticalPercentToHorizontalPercent(
  verticalPercent: number
): number {
  const middleSpan =
    FULL_PERCENT - OVERFLOW_CAP_ROW_COUNT * OVERFLOW_CAP_WIDTH_PERCENT;
  return (
    OVERFLOW_CAP_WIDTH_PERCENT +
    ((FULL_PERCENT - verticalPercent) / FULL_PERCENT) * middleSpan
  );
}

function stopValueHorizontalPercent(
  stop: { value: string },
  stops: readonly { value: string }[]
): number {
  const range = stopValueRange(stops);
  return verticalPercentToHorizontalPercent(
    valueToVerticalPercent(parseStopValue(stop.value), range)
  );
}

function heatmapHorizontalGradientCss(
  stops: readonly { id: string; color: string; value: string }[],
  overflow: { above: string; below: string }
): string {
  const capPercent = OVERFLOW_CAP_WIDTH_PERCENT;
  const middleEnd = FULL_PERCENT - capPercent;

  const layoutStops = stops
    .map((stop) => ({
      color: stop.color,
      pct: stopValueHorizontalPercent(stop, stops),
    }))
    .sort((a, b) => a.pct - b.pct);

  const firstInRangeColor = layoutStops[0]?.color ?? overflow.above;
  const lastInRangeColor = layoutStops.at(-1)?.color ?? overflow.below;

  const parts: string[] = [
    `${overflow.below} 0%`,
    `${overflow.below} ${capPercent.toFixed(1)}%`,
    `${firstInRangeColor} ${capPercent.toFixed(1)}%`,
    ...layoutStops.map((stop) => `${stop.color} ${stop.pct.toFixed(1)}%`),
    `${lastInRangeColor} ${middleEnd.toFixed(1)}%`,
    `${overflow.above} ${middleEnd.toFixed(1)}%`,
    `${overflow.above} ${FULL_PERCENT.toFixed(1)}%`,
  ];

  return `linear-gradient(to right, ${parts.join(', ')})`;
}

function heatmapHorizontalSteppedCss(
  stops: readonly { id: string; color: string; value: string }[],
  overflow: { above: string; below: string }
): string {
  const capPercent = OVERFLOW_CAP_WIDTH_PERCENT;
  const middleEnd = FULL_PERCENT - capPercent;
  const sorted = [...stops].sort(
    (a, b) => parseStopValue(a.value) - parseStopValue(b.value)
  );
  const anchorPcts = sorted.map((stop) =>
    stopValueHorizontalPercent(stop, stops)
  );
  const topBandColor = sorted.at(-1)?.color ?? overflow.above;
  const topAnchorPct = anchorPcts.at(-1) ?? middleEnd;
  const bottomBandColor = sorted[0]?.color ?? overflow.below;

  const parts: string[] = [`${overflow.below} 0%`];

  pushBarHardEdgeHorizontal(parts, overflow.below, bottomBandColor, capPercent);

  for (let index = 0; index < sorted.length; index++) {
    const leftPct = index === 0 ? capPercent : anchorPcts[index - 1];
    const rightPct = anchorPcts[index];
    const stop = sorted[index];
    parts.push(
      `${stop.color} ${leftPct.toFixed(1)}%`,
      `${stop.color} ${rightPct.toFixed(1)}%`
    );
  }

  pushBarHardEdgeHorizontal(parts, topBandColor, overflow.above, topAnchorPct);
  parts.push(`${overflow.above} ${FULL_PERCENT.toFixed(1)}%`);

  return `linear-gradient(to right, ${parts.join(', ')})`;
}

function pushBarHardEdgeHorizontal(
  parts: string[],
  fromColor: string,
  toColor: string,
  pct: number
): void {
  parts.push(`${fromColor} ${pct.toFixed(1)}%`);
  if (fromColor !== toColor) {
    parts.push(`${toColor} ${pct.toFixed(1)}%`);
  }
}

/**
 * Value-proportional horizontal preview ramp with proportional overflow caps.
 * Gradient and stepped share the same stop positions; only bar paint differs.
 */
export function heatmapHorizontalPreviewCss(
  stops: readonly { id: string; color: string; value: string }[],
  textureType: TextureType,
  overflowOverrides?: OverflowColorOverrides
): string {
  if (stops.length === 0) {
    return OVERFLOW_GREY;
  }

  const overflow = overflowStopColors(stops, overflowOverrides, textureType);

  return textureType === 'stepped'
    ? heatmapHorizontalSteppedCss(stops, overflow)
    : heatmapHorizontalGradientCss(stops, overflow);
}
